"use client";
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import { jwtDecode } from "jwt-decode";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { saveAs } from "file-saver";
import ImageModule from "docxtemplater-image-module-free";
import { NumericFormat } from "react-number-format";
import AlertDialog from "./AlertDialog";
import { format, parseISO } from "date-fns";

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#4b5563",
    borderColor: state.isFocused ? "#f97316" : "#6b7280",
    color: "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 1px #f97316" : "none",
    "&:hover": { borderColor: "#f97316" },
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    outline: "none",
    boxShadow: "none",
    border: "none",
    margin: 0,
    padding: 0,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#6b7280" },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    border: "1px solid #4b5563",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e5e7eb",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#4b5563",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "#e5e7eb",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    "&:hover": {
      backgroundColor: "#f97316",
      color: "#fff",
    },
  }),
};

export default function EditMaterialOrderView() {
  const { id, orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [project, setProject] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [surcharges, setSurcharges] = useState([]);
  const [newOrderAttachments, setNewOrderAttachments] = useState([]);
  const [newDeliveryAttachments, setNewDeliveryAttachments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [totalCost, setTotalCost] = useState(0);
  const [isEditingItems, setIsEditingItems] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [quickBooksTaxRates, setQuickBooksTaxRates] = useState([]);
  const [bidAreaFilter, setBidAreaFilter] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sizeFilter, setSizeFilter] = useState(null);
  const userRole = localStorage.getItem("userRole");
  const [users, setUsers] = useState([]);
  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    confirmText: "OK",
    cancelText: "Close",
    onConfirm: () => {},
    onClose: () => setAlert((prev) => ({ ...prev, open: false })),
  });
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailForm, setEmailForm] = useState({
    to: "",
    subject: "",
    body: `Dear Vendor,\n\nPlease find attached the material order for your review and action.\n\nBest regards,`,
    signature: "",
  });
  const [pdfBuffer, setPdfBuffer] = useState(null);

  const sizeOptions = useMemo(() => {
    return [...new Set(filteredItems.map((item) => item.size || "N/A"))].map(
      (size) => ({ value: size, label: size }),
    );
  }, [filteredItems]);

  const bidAreaOptions = useMemo(() => {
    return [
      ...new Set(filteredItems.map((item) => item.bidArea || "(unassigned)")),
    ].map((area) => ({ value: area, label: area }));
  }, [filteredItems]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");
        const decoded = jwtDecode(token);
        setIsAdmin(decoded.role === "admin");
        setUserEmail(decoded.email || "");
        const [
          orderResponse,
          projectResponse,
          vendorsResponse,
          contactsResponse,
          companyProfileResponse,
          userResponse,
          usersResponse,
        ] = await Promise.all([
          axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/materialOrders/${orderId}`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${id}`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            "https://erp-production-72da01c8e651.herokuapp.com/api/accounts/vendors",
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            "https://erp-production-72da01c8e651.herokuapp.com/api/contacts",
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/company-profile`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/users/me/${decoded.email}`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            "https://erp-production-72da01c8e651.herokuapp.com/api/users",
            { headers: { Authorization: `Bearer ${token}` } },
          ),
        ]);
        const fetchedOrder = orderResponse.data;
        setOrder({
          ...fetchedOrder,
          contact: Array.isArray(fetchedOrder.contact)
            ? fetchedOrder.contact.map((c) => c._id || c)
            : fetchedOrder.contact
              ? [fetchedOrder.contact._id || fetchedOrder.contact]
              : [],
          fieldContact: Array.isArray(fetchedOrder.fieldContact)
            ? fetchedOrder.fieldContact.map((u) => u._id || u)
            : fetchedOrder.fieldContact
              ? [fetchedOrder.fieldContact._id || fetchedOrder.fieldContact]
              : [],
          deliveryContact:
            fetchedOrder.deliveryContact?._id ||
            fetchedOrder.deliveryContact ||
            null,
          revision: fetchedOrder.revision || 0, // Ensure revision is included
        });

        setCompanyProfile(companyProfileResponse.data);
        setProject(projectResponse.data);
        setVendors(vendorsResponse.data);
        setContacts(contactsResponse.data);
        setUsers(usersResponse.data);
        setSelectedItems(
          fetchedOrder.items.map((item) => {
            const projectItem = projectResponse.data.projectItems.find(
              (pi) => pi._id === (item.projectItem?._id || item.projectItem),
            );
            return {
              orderItemId: item._id,
              projectItemId: item.projectItem?._id || item.projectItem,
              quantity: item.quantity || 0,
              size: item.size || projectItem?.size || "N/A",
              bidArea: item.bidArea || projectItem?.bidArea || "(unassigned)",
              price: item.price || projectItem?.cost || 0,
            };
          }),
        );
        setSurcharges(fetchedOrder.surcharges || []);
        const contactEmails = contactsResponse.data
          .filter((contact) =>
            (Array.isArray(fetchedOrder.contact)
              ? fetchedOrder.contact.map((c) => c._id || c)
              : [fetchedOrder.contact?._id || fetchedOrder.contact]
            ).includes(contact._id),
          )
          .map((contact) => contact.email)
          .filter((email) => email)
          .join(", ");
        const primaryContact = contactsResponse.data.find((contact) =>
          (Array.isArray(fetchedOrder.contact)
            ? fetchedOrder.contact.map((c) => c._id || c)
            : [fetchedOrder.contact?._id || fetchedOrder.contact]
          ).includes(contact._id),
        );
        const greeting = primaryContact?.firstName
          ? `Hi ${primaryContact.firstName},`
          : `Dear Vendor,`;
        const displayName = userResponse.data.success
          ? userResponse.data.user.displayName || "User"
          : "User";
        setEmailForm({
          to: contactEmails || "",
          subject: `${projectResponse.data.projectName} - PO#${projectResponse.data.projectNumber}-${fetchedOrder.materialOrderNumber}`,
          body: `${greeting}\n\nPlease find attached the material order ${fetchedOrder.materialOrderNumber} for your review and action.\n\nBest regards,`,
          signature: displayName,
        });
      } catch (err) {
        setAlert({
          open: true,
          type: "error",
          title: "Error",
          message: `Failed to load data: ${err.message || "Unknown error"}`,
          confirmText: "OK",
          cancelText: "Close",
          onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
        });
        console.error("Fetch error:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [id, orderId, navigate]);

  useEffect(() => {
    if (!project || !order) return;
    const selectedPO = project.purchaseOrders.find(
      (po) => po._id === (order.purchaseOrder?._id || order.purchaseOrder),
    );
    let items = [];
    if (selectedPO) {
      items = selectedPO.lineItems
        .filter((lineItem) => {
          const vendorId = order.vendor?._id || order.vendor;
          const projectItem =
            project.projectItems.find(
              (pi) => pi._id === lineItem.projectItem,
            ) || {};
          const matchesSearch = searchQuery
            ? (lineItem.title || projectItem.title || "")
                .toLowerCase()
                .includes(searchQuery.toLowerCase()) ||
              (lineItem.itemDescription || projectItem.description || "")
                .toLowerCase()
                .includes(searchQuery.toLowerCase())
            : true;
          const matchesSize = sizeFilter
            ? (lineItem.size || projectItem.size || "N/A") === sizeFilter
            : true;
          const matchesBidArea = bidAreaFilter
            ? lineItem.bidArea === bidAreaFilter
            : true;
          const available =
            (lineItem.requiredQty || lineItem.quantity || 0) -
            (projectItem.quantityOrdered || 0) -
            (projectItem.quantityPending || 0);
          return (
            lineItem.vendor === vendorId &&
            matchesSearch &&
            matchesSize &&
            matchesBidArea &&
            available > 0
          );
        })
        .map((lineItem) => {
          const projectItem =
            project.projectItems.find(
              (pi) => pi._id === lineItem.projectItem,
            ) || {};
          return {
            _id: lineItem.projectItem,
            title: lineItem.title || projectItem.title || "Unknown",
            description:
              lineItem.itemDescription || projectItem.description || "",
            size: lineItem.size || projectItem.size || "N/A",
            quantityRequired: lineItem.requiredQty || lineItem.quantity || 0,
            quantityOrdered: projectItem.quantityOrdered || 0,
            quantityPending: projectItem.quantityPending || 0,
            container: lineItem.container || "",
            bidArea: lineItem.bidArea || "(unassigned)",
            itemType: "material",
            vendor: lineItem.vendor,
            unitCost: lineItem.unitCost || 0,
          };
        });
    }
    setFilteredItems(items);
  }, [project, order, searchQuery, sizeFilter, bidAreaFilter]);
  useEffect(() => {
    const itemsTotal = selectedItems.reduce((sum, item) => {
      const lineItem = filteredItems.find((i) => i._id === item.projectItemId);
      return sum + item.quantity * (item.price || lineItem?.unitCost || 0);
    }, 0);
    const surchargesTotal = surcharges.reduce(
      (sum, surcharge) => sum + (surcharge.price || 0),
      0,
    );
    setTotalCost(itemsTotal + surchargesTotal);
  }, [selectedItems, filteredItems, surcharges]);

  const handleConfirmOrder = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `https://erp-production-72da01c8e651.herokuapp.com/api/materialOrders/${orderId}/status`,
        { status: "confirmed" },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (response.data.success === false) {
        throw new Error(response.data.message || "Failed to confirm order");
      }
      setOrder(response.data.materialOrder);
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "Order confirmed successfully!",
        confirmText: "OK",
        cancelText: "Close",
        onConfirm: () =>
          navigate(`/projects/${id}`, { state: { initialTab: "Materials" } }),
      });
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || err.message || "Unknown error",
        confirmText: "OK",
        cancelText: "Close",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
      console.error("Confirm error:", err);
    }
  };

  const handleVoidOrder = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `https://erp-production-72da01c8e651.herokuapp.com/api/materialOrders/${orderId}/status`,
        { status: "void" },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (response.data.success === false) {
        throw new Error(response.data.message || "Failed to void order");
      }
      setOrder(response.data.materialOrder);
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "Order voided successfully!",
        confirmText: "OK",
        cancelText: "Close",
        onConfirm: () =>
          navigate(`/projects/${id}`, { state: { initialTab: "Materials" } }),
      });
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || err.message || "Unknown error",
        confirmText: "OK",
        cancelText: "Close",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
      console.error("Void error:", err);
    }
  };

  const handleOrderChange = (e) => {
    const { name, value } = e.target;
    if (name === "expectedDeliveryDate") {
      setOrder((prev) => ({
        ...prev,
        [name]: parseDateForDatabase(value),
      }));
    } else {
      setOrder((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleOrderFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map((file) => ({
      name: file.name,
      file,
    }));
    setNewOrderAttachments((prev) => [...prev, ...newFiles]);
  };

  const handleDeliveryFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map((file) => ({
      name: file.name,
      file,
    }));
    setNewDeliveryAttachments((prev) => [...prev, ...newFiles]);
  };

  const handleContactChange = (selected) => {
    setOrder((prev) => ({
      ...prev,
      contact: selected ? selected.map((option) => option.value) : [],
    }));
  };

  const handleVendorChange = (selected) => {
    setOrder((prev) => ({
      ...prev,
      vendor: selected ? selected.value : "",
    }));
  };

  const handleFieldContactChange = (selected) => {
    setOrder((prev) => ({
      ...prev,
      fieldContact: selected ? selected.map((option) => option.value) : [],
    }));
  };

  const handleDeliveryContactChange = (selected) => {
    setOrder((prev) => ({
      ...prev,
      deliveryContact: selected ? selected.value : null,
    }));
  };

  const handleItemSelect = (projectItemId) => {
    setSelectedItems((prev) => {
      const item = filteredItems.find((i) => i._id === projectItemId);
      const existingOrderItem = order.items.find(
        (oi) => (oi.projectItem?._id || oi.projectItem) === projectItemId,
      );
      if (prev.some((i) => i.projectItemId === projectItemId)) {
        return prev.filter((i) => i.projectItemId !== projectItemId);
      } else {
        const selectedPO = project.purchaseOrders.find(
          (po) => po._id === (order.purchaseOrder?._id || order.purchaseOrder),
        );
        if (
          order.purchaseOrder &&
          !selectedPO?.lineItems.some(
            (lineItem) =>
              String(lineItem.projectItem) === String(projectItemId),
          )
        ) {
          setAlert({
            open: true,
            type: "error",
            title: "Selection Error",
            message: "Can only select items under the same PO.",
            confirmText: "OK",
            cancelText: "Close",
            onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
          });
          return prev;
        }
        if (item.vendor !== (order.vendor?._id || order.vendor)) {
          setAlert({
            open: true,
            type: "error",
            title: "Selection Error",
            message: "Can only add items from the same vendor as the order.",
            confirmText: "OK",
            cancelText: "Close",
            onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
          });
          return prev;
        }
        return [
          ...prev,
          {
            orderItemId:
              existingOrderItem?._id || `new-${projectItemId}-${Date.now()}`,
            projectItemId,
            quantity: existingOrderItem?.quantity || 0,
            size: existingOrderItem?.size || item.size || "N/A",
            bidArea: item.bidArea || "(unassigned)",
            price: existingOrderItem?.price || item.unitCost || 0,
          },
        ];
      }
    });
  };

  const handleQuantityChange = (projectItemId, values) => {
    const item = filteredItems.find((i) => i._id === projectItemId);
    const selectedItem = selectedItems.find(
      (i) => i.projectItemId === projectItemId,
    );
    const orderItem = order.items.find(
      (oi) => (oi.projectItem?._id || oi.projectItem) === projectItemId,
    );
    const initialQuantity = orderItem ? orderItem.quantity : 0;
    const maxOrderable = item.quantityRequired - item.quantityOrdered;
    const maxAllowed = maxOrderable + initialQuantity;
    const quantity = Math.min(
      Math.max(0, values.floatValue || 0),
      maxOrderable,
    );
    if (quantity > maxOrderable) {
      setAlert({
        open: true,
        type: "error",
        title: "Quantity Error",
        message: `Quantity for ${item.title} cannot exceed ${maxOrderable}`,
        confirmText: "OK",
        cancelText: "Close",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
    }
    setSelectedItems((prev) =>
      prev.map((i) =>
        i.projectItemId === projectItemId ? { ...i, quantity } : i,
      ),
    );
  };

  const handlePriceChange = (projectItemId, values) => {
    const price = values.floatValue || 0;
    setSelectedItems((prev) =>
      prev.map((i) =>
        i.projectItemId === projectItemId ? { ...i, price } : i,
      ),
    );
  };

  const handleBidAreaChange = (projectItemId, selected) => {
    setSelectedItems((prev) =>
      prev.map((i) =>
        i.projectItemId === projectItemId
          ? { ...i, bidArea: selected ? selected.value : "" }
          : i,
      ),
    );
  };

  const handleAddSurcharge = () => {
    setSurcharges((prev) => [...prev, { description: "", price: 0 }]);
  };

  const handleSurchargeChange = (index, field, value) => {
    setSurcharges((prev) =>
      prev.map((surcharge, i) =>
        i === index
          ? {
              ...surcharge,
              [field]:
                field === "price"
                  ? value.floatValue !== undefined
                    ? value.floatValue
                    : 0
                  : value,
            }
          : surcharge,
      ),
    );
  };

  const handleRemoveSurcharge = (index) => {
    setSurcharges((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateOrder = async () => {
    try {
      const token = localStorage.getItem("token");
      if (order.revision < 0) {
        setAlert({
          open: true,
          type: "error",
          title: "Invalid Revision",
          message: "Revision must be a non-negative integer",
          confirmText: "OK",
          cancelText: "Close",
          onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
        });
        return;
      }

      // Update quantity pending
      const quantityPendingChanges = [];
      const originalItems = order.items || [];
      const deselectedItems = originalItems.filter(
        (oi) =>
          !selectedItems.some(
            (si) =>
              si.projectItemId === (oi.projectItem?._id || oi.projectItem),
          ),
      );
      deselectedItems.forEach((item) => {
        quantityPendingChanges.push({
          projectItemId: item.projectItem?._id || item.projectItem,
          quantityChange: -(item.quantity || 0),
        });
      });
      selectedItems.forEach((selectedItem) => {
        const originalItem = originalItems.find(
          (oi) =>
            (oi.projectItem?._id || oi.projectItem) ===
            selectedItem.projectItemId,
        );
        const originalQuantity = originalItem ? originalItem.quantity : 0;
        const quantityDifference = selectedItem.quantity - originalQuantity;
        if (quantityDifference !== 0) {
          quantityPendingChanges.push({
            projectItemId: selectedItem.projectItemId,
            quantityChange: quantityDifference,
          });
        }
      });

      for (const change of quantityPendingChanges) {
        const response = await axios.patch(
          `https://erp-production-72da01c8e651.herokuapp.com/api/materialOrders/projects/${id}/items/${change.projectItemId}/quantityPending`,
          { quantityChange: change.quantityChange, materialOrderId: orderId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
        if (response.data.success === false) {
          throw new Error(
            response.data.message || "Failed to update quantity pending",
          );
        }
      }

      const updatedItems = selectedItems.map((selectedItem) => ({
        _id: selectedItem.orderItemId,
        projectItem: selectedItem.projectItemId,
        quantity: selectedItem.quantity,
        size: selectedItem.size,
        bidArea: selectedItem.bidArea,
        price: selectedItem.price,
      }));

      const updatedOrder = {
        purchaseOrder: order.purchaseOrder._id || order.purchaseOrder,
        vendor: order.vendor._id || order.vendor,
        poNumber: order.poNumber,
        shippingMethod: order.shippingMethod,
        revision: order.revision,
        deliveryInstructions: order.deliveryInstructions,
        fieldContact: order.fieldContact,
        deliveryContact: order.deliveryContact,
        contact: order.contact,
        paid: order.paid,
        paidDate: order.paidDate,
        expectedDeliveryDate: order.expectedDeliveryDate,
        surcharges,
        items: updatedItems,
      };

      const response = await axios.put(
        `https://erp-production-72da01c8e651.herokuapp.com/api/materialOrders/update/${orderId}`,
        updatedOrder,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data.success === false) {
        throw new Error(response.data.message || "Failed to update order");
      }

      const updatedOrderData = response.data;

      // Normalize contact, fieldContact, and deliveryContact to IDs
      const normalizedOrder = {
        ...updatedOrderData,
        revision: updatedOrderData.revision || 0,
        contact: Array.isArray(updatedOrderData.contact)
          ? updatedOrderData.contact.map((c) => c._id || c)
          : updatedOrderData.contact
            ? [updatedOrderData.contact._id || updatedOrderData.contact]
            : [],
        fieldContact: Array.isArray(updatedOrderData.fieldContact)
          ? updatedOrderData.fieldContact.map((u) => u._id || u)
          : updatedOrderData.fieldContact
            ? [
                updatedOrderData.fieldContact._id ||
                  updatedOrderData.fieldContact,
              ]
            : [],
        deliveryContact:
          updatedOrderData.deliveryContact?._id ||
          updatedOrderData.deliveryContact ||
          null,
      };

      setOrder(normalizedOrder);

      setSelectedItems(
        updatedOrderData.items.map((item) => {
          const projectItem = project.projectItems.find(
            (pi) => pi._id === (item.projectItem?._id || item.projectItem),
          );
          return {
            orderItemId: item._id,
            projectItemId: item.projectItem?._id || item.projectItem,
            quantity: item.quantity || 0,
            size: item.size || projectItem?.size || "N/A",
            bidArea: item.bidArea || projectItem?.bidArea || "(unassigned)",
            price: item.price || projectItem?.cost || 0,
          };
        }),
      );

      // Handle order attachments
      if (newOrderAttachments.length > 0) {
        const orderAttachmentFormData = new FormData();
        orderAttachmentFormData.append(
          "existingOrderAttachments",
          JSON.stringify(order.orderAttachments || []),
        );
        newOrderAttachments.forEach((att) => {
          orderAttachmentFormData.append("orderAttachments", att.file);
        });
        const orderAttachmentResponse = await axios.put(
          `https://erp-production-72da01c8e651.herokuapp.com/api/materialOrders/${orderId}/order-attachments`,
          orderAttachmentFormData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          },
        );
        if (orderAttachmentResponse.data.success === false) {
          throw new Error(
            orderAttachmentResponse.data.message ||
              "Failed to update order attachments",
          );
        }
        setOrder({
          ...orderAttachmentResponse.data,
          contact: Array.isArray(orderAttachmentResponse.data.contact)
            ? orderAttachmentResponse.data.contact.map((c) => c._id || c)
            : orderAttachmentResponse.data.contact
              ? [
                  orderAttachmentResponse.data.contact._id ||
                    orderAttachmentResponse.data.contact,
                ]
              : [],
          fieldContact: Array.isArray(orderAttachmentResponse.data.fieldContact)
            ? orderAttachmentResponse.data.fieldContact.map((u) => u._id || u)
            : orderAttachmentResponse.data.fieldContact
              ? [
                  orderAttachmentResponse.data.fieldContact._id ||
                    orderAttachmentResponse.data.fieldContact,
                ]
              : [],
          deliveryContact:
            orderAttachmentResponse.data.deliveryContact?._id ||
            orderAttachmentResponse.data.deliveryContact ||
            null,
        });
      }

      // Handle delivery attachments
      if (newDeliveryAttachments.length > 0) {
        const deliveryAttachmentFormData = new FormData();
        deliveryAttachmentFormData.append(
          "existingDeliveryAttachments",
          JSON.stringify(order.deliveryAttachments || []),
        );
        newDeliveryAttachments.forEach((att) => {
          deliveryAttachmentFormData.append("deliveryAttachments", att.file);
        });
        const deliveryAttachmentResponse = await axios.put(
          `https://erp-production-72da01c8e651.herokuapp.com/api/materialOrders/${orderId}/delivery-attachments`,
          deliveryAttachmentFormData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          },
        );
        if (deliveryAttachmentResponse.data.success === false) {
          throw new Error(
            deliveryAttachmentResponse.data.message ||
              "Failed to update delivery attachments",
          );
        }
        setOrder({
          ...deliveryAttachmentResponse.data,
          contact: Array.isArray(deliveryAttachmentResponse.data.contact)
            ? deliveryAttachmentResponse.data.contact.map((c) => c._id || c)
            : deliveryAttachmentResponse.data.contact
              ? [
                  deliveryAttachmentResponse.data.contact._id ||
                    deliveryAttachmentResponse.data.contact,
                ]
              : [],
          fieldContact: Array.isArray(
            deliveryAttachmentResponse.data.fieldContact,
          )
            ? deliveryAttachmentResponse.data.fieldContact.map(
                (u) => u._id || u,
              )
            : deliveryAttachmentResponse.data.fieldContact
              ? [
                  deliveryAttachmentResponse.data.fieldContact._id ||
                    deliveryAttachmentResponse.data.fieldContact,
                ]
              : [],
          deliveryContact:
            deliveryAttachmentResponse.data.deliveryContact?._id ||
            deliveryAttachmentResponse.data.deliveryContact ||
            null,
        });
      }

      setNewOrderAttachments([]);
      setNewDeliveryAttachments([]);
      setIsEditingItems(false);
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "Order updated successfully!",
        confirmText: "OK",
        cancelText: "Close",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || err.message || "Unknown error",
        confirmText: "OK",
        cancelText: "Close",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
      console.error("Update error:", err);
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    const formatted = `${year}-${month}-${day}`;
    console.log("formatDateForInput input:", dateString, "output:", formatted);
    return formatted;
  };

  const parseDateForDatabase = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return date.toISOString();
  };

  const userOptions = users.map((user) => ({
    value: user._id,
    label: `${user.displayName || "Unnamed User"} (${
      user.phoneNumber || "No phone"
    })`,
  }));

  const handleToggleEditItems = () => {
    if (isEditingItems) {
      setOrder((prev) => {
        const updatedItems = selectedItems.map((selectedItem) => {
          const filteredItem = filteredItems.find(
            (fi) => fi._id === selectedItem.projectItemId,
          );
          const projectItem = project.projectItems.find(
            (pi) => pi._id === selectedItem.projectItemId,
          );
          const title = filteredItem?.title || projectItem?.title || "Unknown";
          const container =
            filteredItem?.container || projectItem?.container || "N/A";
          return {
            _id: selectedItem.orderItemId,
            projectItem: {
              _id: selectedItem.projectItemId,
              title: title,
              container: container,
            },
            quantity: selectedItem.quantity,
            size: selectedItem.size,
            bidArea: selectedItem.bidArea,
            price: selectedItem.price,
          };
        });
        return {
          ...prev,
          items: updatedItems,
        };
      });
    }
    setIsEditingItems(!isEditingItems);
  };

  const generateDocx = async (sendEmail = false) => {
    try {
      const response = await fetch("/material_order_template.docx");
      if (!response.ok) {
        throw new Error(`Failed to load template: ${response.statusText}`);
      }
      const templateArrayBuffer = await response.arrayBuffer();
      const zip = new PizZip(templateArrayBuffer);
      const base64Regex =
        /^(?:data:)?image\/(png|jpg|jpeg|svg|svg\+xml);base64,/;
      const validBase64 =
        /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
      function base64Parser(tagValue) {
        if (typeof tagValue !== "string" || !base64Regex.test(tagValue)) {
          console.log(
            "Base64 parsing failed: Invalid format or not a string",
            tagValue,
          );
          return null;
        }
        const stringBase64 = tagValue.replace(base64Regex, "");
        if (!validBase64.test(stringBase64)) {
          throw new Error(
            "Error parsing base64 data, your data contains invalid characters",
          );
        }
        const binaryString = window.atob(stringBase64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
      }
      let imageBase64;
      if (
        companyProfile?.logo &&
        !companyProfile.logo.startsWith("data:image")
      ) {
        const logoResponse = await fetch(companyProfile.logo);
        if (logoResponse.ok) {
          const logoBlob = await logoResponse.blob();
          const reader = new FileReader();
          imageBase64 = await new Promise((resolve) => {
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(logoBlob);
          });
        } else {
          console.warn("Failed to fetch logo, skipping image");
        }
      } else if (companyProfile?.logo) {
        imageBase64 = companyProfile.logo;
      }
      const imageOptions = {
        getImage: (tagValue) => base64Parser(tagValue),
        getSize: () => [125, 90],
      };
      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        modules: [new ImageModule(imageOptions)],
      });
      const vendor = vendors.find(
        (v) => v._id === (order.vendor?._id || order.vendor),
      );
      const vendorName = vendor?.companyName || "Unknown Vendor";
      const vendorAddress = vendor?.address
        ? `${vendor.address.street},${vendor.address.city},${vendor.address.state} ${vendor.address.postalCode},${vendor.address.country}`
        : "N/A";
      const vendorContact = vendor?.contact || "N/A";
      const itemsSubtotal = selectedItems.reduce((sum, item) => {
        const filteredItem = filteredItems.find(
          (i) => i._id === item.projectItemId,
        );
        return (
          sum + item.quantity * (item.price || filteredItem?.unitCost || 0)
        );
      }, 0);
      const surchargesTotal = surcharges.reduce(
        (sum, surcharge) => sum + (surcharge.price || 0),
        0,
      );
      const subtotal = itemsSubtotal + surchargesTotal;
      let taxRate = 0;
      let taxCodeId = "";
      if (companyProfile?.plugins?.quickbooks?.connected) {
        taxCodeId =
          companyProfile.defaultTax.taxCodeId ||
          quickBooksTaxRates[0]?.id ||
          "";
        taxRate = parseFloat(companyProfile.defaultTax.taxRate || 0);
      } else {
        taxRate = parseFloat(companyProfile?.defaultTax.taxRate) || 0;
      }
      const hst = subtotal * (taxRate / 100);
      const totalWithTax = subtotal + hst;

      const items = [
        ...selectedItems.map((item) => {
          const filteredItem = filteredItems.find(
            (i) => i._id === item.projectItemId,
          );
          const projectItem =
            project.projectItems.find((pi) => pi._id === item.projectItemId) ||
            {};
          return {
            materialTitle: projectItem.description,
            qtyOrdered: item.quantity.toString(),
            price: `$${item.price.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`,
            size: item.size || "N/A",
            container:
              filteredItem?.container || projectItem.container || "N/A",
            bidArea: item.bidArea || "(unassigned)",
          };
        }),
        ...surcharges.map((surcharge) => ({
          materialTitle: surcharge.description || "Surcharge",
          qtyOrdered: "N/A",
          price: `$${surcharge.price.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`,
          container: "N/A",
          bidArea: "N/A",
        })),
      ];
      const companyName = companyProfile?.companyName || "Your Company Name";
      const companyAddress = companyProfile?.address
        ? `${companyProfile.address.street}, ${companyProfile.address.city}, ${companyProfile.address.state} ${companyProfile.address.zip}, ${companyProfile.address.country}`
        : "N/A";
      const companyPhone = companyProfile?.phone || "N/A";
      const projectName = project?.projectName || "Unknown Project";
      const projectLocation = project?.address
        ? `${project.address.street}, ${project.address.city}, ${project.address.state} ${project.address.postalCode}, ${project.address.country}`
        : project?.location || "N/A";
      const projectStartDate = project?.startDate
        ? new Date(project.startDate).toLocaleDateString()
        : "N/A";
      const expectedDeliveryDate = order?.expectedDeliveryDate
        ? new Date(order.expectedDeliveryDate).toLocaleDateString()
        : "N/A";
      const createdAt = order?.createdAt
        ? new Date(order.createdAt).toLocaleDateString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })
        : "N/A";
      const poNumberDisplay = `${project.projectNumber}`;
      const fieldContacts = users
        .filter((user) => order.fieldContact.includes(user._id))
        .map((user) => ({
          name: user.displayName || "Unnamed User",
          phone: user.phoneNumber || "N/A",
        }));
      const fieldContactsText =
        fieldContacts.map((fc) => `${fc.name} (${fc.phone})`).join(", ") ||
        "N/A";
      const deliveryContact = order.deliveryContact
        ? users.find((user) => user._id === order.deliveryContact)
        : null;
      const deliveryContactText = deliveryContact
        ? `${deliveryContact.displayName || "Unnamed User"} (${
            deliveryContact.phoneNumber || "No phone"
          })`
        : "N/A";
      const data = {
        companyName,
        companyAddress,
        companyPhone,
        projectName,
        projectLocation,
        projectStartDate,
        vendorName,
        vendorAddress,
        vendorContact,
        materialOrderNumber: order.materialOrderNumber,
        poNumber: poNumberDisplay,
        items,
        shippingMethod: order.shippingMethod,
        deliveryInstructions: order.deliveryInstructions || "N/A",
        totalCost: `$${totalWithTax.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
        taxRate: `${taxRate}%`,
        taxAmount: `$${hst.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
        subtotal: `$${subtotal.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
        image: imageBase64,
        expectedDeliveryDate,
        createdAt,
        fieldContacts: fieldContactsText,
        deliveryContact: deliveryContactText,
        revision: order.revision ? `R${order.revision}` : "", // Fallback to "R0" if
      };
      doc.setData(data);
      doc.render();
      const docxBlob = doc.getZip().generate({
        type: "blob",
        mimeType:
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const formData = new FormData();
      formData.append(
        "docx",
        docxBlob,
        `MaterialOrder_${order.materialOrderNumber}.docx`,
      );
      formData.append("documentType", "MaterialOrder");
      formData.append("identifier", order.materialOrderNumber);
      const token = localStorage.getItem("token");
      const pdfResponse = await axios.post(
        "https://erp-production-72da01c8e651.herokuapp.com/api/upload/convert-to-pdf",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        },
      );
      if (
        pdfResponse.status === 200 &&
        pdfResponse.headers["content-type"] === "application/pdf"
      ) {
        const pdfBlob = new Blob([pdfResponse.data], {
          type: "application/pdf",
        });
        if (sendEmail) {
          setPdfBuffer(pdfBlob);
          setShowEmailForm(true);
          setAlert({
            open: true,
            type: "success",
            title: "Success",
            message:
              "PDF generated successfully! Please fill out the email form.",
            confirmText: "OK",
            cancelText: "Close",
            onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
          });
        } else {
          saveAs(pdfBlob, `MaterialOrder_${order.materialOrderNumber}.pdf`);
          setAlert({
            open: true,
            type: "success",
            title: "Success",
            message: "Order document generated successfully!",
            confirmText: "OK",
            cancelText: "Close",
            onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
          });
        }
      } else {
        const textResponse = await pdfResponse.data.text();
        let errorData;
        try {
          errorData = JSON.parse(textResponse);
        } catch {
          errorData = { message: textResponse };
        }
        throw new Error(errorData.message || "Unknown error from server");
      }
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          err.response?.data?.message ||
          err.message ||
          "Failed to generate or convert document",
        confirmText: "OK",
        cancelText: "Close",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
      console.error("Error:", err);
    }
  };

  const handleEmailSubmit = async () => {
    try {
      if (!emailForm.to || !emailForm.subject || !emailForm.body) {
        setAlert({
          open: true,
          type: "error",
          title: "Invalid Input",
          message: "Please fill in all required email fields",
          confirmText: "OK",
          cancelText: "Close",
          onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
        });
        return;
      }
      const validateEmails = (emails) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emails
          .split(",")
          .map((email) => email.trim())
          .every((email) => emailRegex.test(email));
      };
      if (!validateEmails(emailForm.to)) {
        setAlert({
          open: true,
          type: "error",
          title: "Invalid Email",
          message: "Please enter valid email addresses",
          confirmText: "OK",
          cancelText: "Close",
          onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
        });
        return;
      }
      if (
        !(pdfBuffer instanceof Blob) ||
        pdfBuffer.type !== "application/pdf"
      ) {
        setAlert({
          open: true,
          type: "error",
          title: "Missing or Invalid Attachment",
          message: "A valid PDF file is required",
          confirmText: "OK",
          cancelText: "Close",
          onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
        });
        return;
      }
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("to", emailForm.to);
      formData.append("subject", emailForm.subject);
      formData.append("body", emailForm.body);
      formData.append("signature", emailForm.signature || "");
      formData.append("from", userEmail);
      formData.append(
        "files",
        pdfBuffer,
        `MaterialOrder_${order.materialOrderNumber}.pdf`,
      );
      const response = await axios.post(
        `https://erp-production-72da01c8e651.herokuapp.com/api/materialOrders/${orderId}/send-email`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (response.data.success === false) {
        throw new Error(response.data.message || "Failed to send email");
      }
      setShowEmailForm(false);
      setPdfBuffer(null);
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "Email sent successfully!",
        confirmText: "OK",
        cancelText: "Close",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || err.message || "Unknown error",
        confirmText: "OK",
        cancelText: "Close",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
      console.error("Email error:", err);
    }
  };

  const handleSendEmail = async () => {
    if (!userEmail) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "User email not available. Please log in again.",
        confirmText: "OK",
        cancelText: "Close",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    // Save changes first so contacts, items, etc. are persisted before generating the PDF
    try {
      await handleUpdateOrder();
    } catch (err) {
      console.error("Pre-email save error:", err);
      return;
    }

    // Refresh email form "To" field based on the now-saved contacts
    const contactEmails = contacts
      .filter((c) => order.contact.includes(c._id))
      .map((c) => c.email)
      .filter((email) => email)
      .join(", ");

    const primaryContact = contacts.find((c) => order.contact.includes(c._id));
    const greeting = primaryContact?.firstName
      ? `Hi ${primaryContact.firstName},`
      : `Dear Vendor,`;

    setEmailForm((prev) => ({
      ...prev,
      to: contactEmails || prev.to,
      subject: `${project.projectName} - PO#${project.projectNumber}-${order.materialOrderNumber}`,
      body: `${greeting}\n\nPlease find attached the material order for your review and action.\n\nBest regards,`,
    }));

    generateDocx(true);
  };

  const vendorOptions = vendors.map((vendor) => ({
    value: vendor._id,
    label: vendor.companyName || "Unnamed Vendor",
  }));

  const poOptions =
    project?.purchaseOrders?.map((po) => ({
      value: po._id,
      label: `${po.title} (PO# ${po.poNumber})`,
    })) || [];

  const contactOptions = contacts
    .filter((contact) => {
      const vendorId = order?.vendor?._id || order?.vendor;
      return contact.relatedAccount === vendorId;
    })
    .map((contact) => ({
      value: contact._id,
      label: `${contact.firstName} ${contact.lastName} (${contact.email})`,
    }));

  if (isLoading)
    return (
      <div className="text-center py-4 text-gray-200 bg-gray-700">
        Loading...
      </div>
    );

  if (!order || !project)
    return (
      <div className="text-center py-4 text-gray-200 bg-gray-700">
        No data available
      </div>
    );

  return (
    <div className=" mx-auto px-4 py-6">
      <AlertDialog
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        confirmText={alert.confirmText}
        cancelText={alert.cancelText}
        onClose={alert.onClose}
        onConfirm={alert.onConfirm}
      />
      <div className="bg-gray-700 p-6 rounded-lg border border-gray-600">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold text-white mb-6">
            Edit Material Order: {order.materialOrderNumber} -{" "}
            {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
          </h1>
          <button
            type="button"
            onClick={() =>
              navigate(`/projects/${id}`, {
                state: { initialTab: "Materials" },
              })
            }
            className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
          >
            Back
          </button>
        </div>
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Purchase Order
              </label>
              <Select
                options={poOptions}
                value={poOptions.find(
                  (option) =>
                    option.value ===
                    (order.purchaseOrder?._id || order.purchaseOrder),
                )}
                isDisabled
                className="mt-1"
                styles={customStyles}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Vendor
              </label>
              <Select
                options={vendorOptions}
                value={vendorOptions.find(
                  (option) =>
                    option.value === (order.vendor?._id || order.vendor),
                )}
                onChange={handleVendorChange}
                placeholder="Select Vendor"
                className="mt-1"
                isDisabled
                styles={customStyles}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                PO Number
              </label>
              <input
                type="text"
                name="poNumber"
                value={order.poNumber}
                onChange={handleOrderChange}
                className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                disabled
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Material Order Number
              </label>
              <input
                type="text"
                value={order.materialOrderNumber}
                readOnly
                className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Revision
              </label>
              <NumericFormat
                value={order.revision || 0}
                onValueChange={(values) =>
                  setOrder({ ...order, revision: values.floatValue || 0 })
                }
                allowNegative={false}
                decimalScale={0}
                thousandSeparator=","
                className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Shipping Method
              </label>
              <select
                name="shippingMethod"
                value={order.shippingMethod}
                onChange={handleOrderChange}
                className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              >
                <option value="Delivery">Delivery</option>
                <option value="Pickup">Pickup</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Paid
              </label>
              <select
                name="paid"
                value={order.paid ? "true" : "false"}
                onChange={(e) =>
                  setOrder({ ...order, paid: e.target.value === "true" })
                }
                className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              >
                <option value="true">Paid</option>
                <option value="false">Not Paid</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Paid Date
              </label>
              <input
                type="date"
                name="paidDate"
                value={order.paidDate ? order.paidDate.split("T")[0] : ""}
                onChange={handleOrderChange}
                className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                disabled={!order.paid}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Expected Delivery Date
              </label>
              <input
                type="date"
                name="expectedDeliveryDate"
                value={formatDateForInput(order.expectedDeliveryDate)}
                onChange={handleOrderChange}
                className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Contacts
              </label>
              <Select
                options={contactOptions}
                value={contactOptions.filter((option) =>
                  order.contact.includes(option.value),
                )}
                onChange={handleContactChange}
                placeholder="Select Contacts"
                className="mt-1"
                styles={customStyles}
                isMulti
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Field Contacts
              </label>
              <Select
                options={userOptions}
                value={userOptions.filter((option) =>
                  order.fieldContact.includes(option.value),
                )}
                onChange={handleFieldContactChange}
                placeholder="Select Field Contacts"
                className="mt-1"
                styles={customStyles}
                isMulti
              />
            </div>
            {order.shippingMethod === "Pickup" && (
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Delivery Driver Contact
                </label>
                <Select
                  options={userOptions}
                  value={
                    userOptions.find(
                      (option) => option.value === order.deliveryContact,
                    ) || null
                  }
                  onChange={handleDeliveryContactChange}
                  placeholder="Select Delivery Contact"
                  className="mt-1"
                  styles={customStyles}
                  isClearable
                />
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400">
              Delivery Instructions
            </label>
            <textarea
              name="deliveryInstructions"
              value={order.deliveryInstructions || ""}
              onChange={handleOrderChange}
              className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
            />
          </div>
          <div>
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-sm font-medium text-gray-400">Order Items</h4>
              <div className="flex items-center space-x-4">
                {isEditingItems && (
                  <div className="flex flex-wrap items-center space-x-4 mb-4">
                    <div className="w-48">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search title or description"
                        className="w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      />
                    </div>
                    <div className="w-48">
                      <Select
                        key={`size-filter-${sizeFilter || "null"}`}
                        options={sizeOptions}
                        value={
                          sizeOptions.find(
                            (option) => option.value === sizeFilter,
                          ) || null
                        }
                        onChange={(selected) =>
                          setSizeFilter(selected ? selected.value : null)
                        }
                        placeholder="Filter by Size"
                        styles={customStyles}
                        isClearable
                      />
                    </div>
                    <div className="w-48">
                      <Select
                        key={`bid-area-filter-${bidAreaFilter || "null"}`}
                        options={bidAreaOptions}
                        value={
                          bidAreaOptions.find(
                            (option) => option.value === bidAreaFilter,
                          ) || null
                        }
                        onChange={(selected) =>
                          setBidAreaFilter(selected ? selected.value : null)
                        }
                        placeholder="Filter by Bid Area"
                        styles={customStyles}
                        isClearable
                      />
                    </div>
                    <div>
                      <button
                        type="button"
                        onClick={() => {
                          setSearchQuery("");
                          setSizeFilter(null);
                          setBidAreaFilter(null);
                        }}
                        className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
                      >
                        Clear Filters
                      </button>
                    </div>
                  </div>
                )}
                <button
                  onClick={handleToggleEditItems}
                  className="px-4 py-2 text-sm font-medium text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                >
                  {isEditingItems ? "Done Editing" : "Edit Items"}
                </button>
              </div>
            </div>
            {!isEditingItems ? (
              <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Qty Ordered
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Container
                    </th>
                    {userRole === "foreman" ? (
                      ""
                    ) : (
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Price
                      </th>
                    )}
                    {userRole === "foreman" ? (
                      ""
                    ) : (
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Line Item Total
                      </th>
                    )}
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Bid Area
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-600">
                  {Array.isArray(order.items) && order.items.length > 0 ? (
                    order.items.map((item) => {
                      const projectItem = project.projectItems.find(
                        (pi) =>
                          pi._id ===
                          (item.projectItem?._id || item.projectItem),
                      );
                      return (
                        <tr
                          key={item._id}
                          className="hover:bg-gray-500 transition-colors duration-200"
                        >
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                            {projectItem?.description || "Unknown"}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                            {item.size || "N/A"}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                            {item.quantity}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                            {item.projectItem?.container ||
                              projectItem?.container ||
                              "N/A"}
                          </td>
                          {userRole === "foreman" ? (
                            ""
                          ) : (
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                              ${item.price?.toFixed(2) || "0.00"}
                            </td>
                          )}
                          {userRole === "foreman" ? (
                            ""
                          ) : (
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                              $
                              {(item.quantity * item.price).toFixed(2) ||
                                "0.00"}
                            </td>
                          )}
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                            {item.bidArea || "N/A"}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan="7"
                        className="px-4 py-2 text-sm text-gray-400 text-center"
                      >
                        No items available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider sticky left-0 bg-gray-800">
                        Select
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Size
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Vendor
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Qty Req
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Qty Ord
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Qty Pend
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Qty to Order
                      </th>
                      {userRole === "foreman" ? (
                        ""
                      ) : (
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Price
                        </th>
                      )}
                      {userRole === "foreman" ? (
                        ""
                      ) : (
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Line Item Total
                        </th>
                      )}
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Container
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Bid Area
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-600">
                    {filteredItems.map((item) => {
                      const selectedItem = selectedItems.find(
                        (i) => i.projectItemId === item._id,
                      );
                      const orderItem = order.items.find(
                        (oi) =>
                          (oi.projectItem?._id || oi.projectItem) === item._id,
                      );
                      const initialQuantity = orderItem
                        ? orderItem.quantity
                        : 0;
                      const maxOrderable =
                        item.quantityRequired - item.quantityOrdered;
                      const maxAllowed = maxOrderable + initialQuantity;
                      const vendor = vendors.find((v) => v._id === item.vendor);
                      const lineItemTotal = selectedItem
                        ? (selectedItem.quantity * selectedItem.price).toFixed(
                            2,
                          )
                        : orderItem
                          ? (orderItem.quantity * orderItem.price).toFixed(2)
                          : "0.00";
                      return (
                        <tr
                          key={item._id}
                          className={
                            selectedItem
                              ? "bg-orange-600/20"
                              : "hover:bg-gray-500 transition-colors duration-200"
                          }
                        >
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200 sticky left-0 bg-gray-700">
                            <input
                              type="checkbox"
                              checked={!!selectedItem}
                              onChange={() => handleItemSelect(item._id)}
                              className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                            />
                          </td>
                          <td
                            className="px-4 py-2 text-sm text-gray-200 truncate"
                            title={item.title || "N/A"}
                          >
                            {item.description}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                            {item?.size || "N/A"}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                            {vendor?.companyName || "Unknown"}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                            {item.quantityRequired}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                            {item.quantityOrdered}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                            {item.quantityPending}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                            <NumericFormat
                              value={selectedItem ? selectedItem.quantity : ""}
                              onValueChange={(values) =>
                                handleQuantityChange(item._id, values)
                              }
                              disabled={!selectedItem}
                              allowNegative={false}
                              decimalScale={0}
                              thousandSeparator=","
                              placeholder="0"
                              className="w-20 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                              isAllowed={(values) => {
                                const { floatValue = 0 } = values;
                                return floatValue <= maxAllowed;
                              }}
                            />
                          </td>
                          {userRole === "foreman" ? (
                            ""
                          ) : (
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                              <div className="relative">
                                <NumericFormat
                                  value={
                                    selectedItem
                                      ? selectedItem.price
                                      : (orderItem?.price ?? "")
                                  }
                                  onValueChange={(values) =>
                                    handlePriceChange(item._id, values)
                                  }
                                  disabled={!selectedItem}
                                  allowNegative={false}
                                  decimalScale={2}
                                  fixedDecimalScale
                                  thousandSeparator=","
                                  prefix="$"
                                  className="w-24 pl-6 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                                />
                              </div>
                            </td>
                          )}
                          {userRole === "foreman" ? (
                            ""
                          ) : (
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                              $
                              {lineItemTotal.toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                          )}
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                            {item.container || "N/A"}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                            <Select
                              options={bidAreaOptions}
                              value={
                                bidAreaOptions.find(
                                  (option) =>
                                    option.value ===
                                    (selectedItem
                                      ? selectedItem.bidArea
                                      : item.bidArea),
                                ) || null
                              }
                              onChange={(selected) =>
                                handleBidAreaChange(item._id, selected)
                              }
                              placeholder="Select Bid Area"
                              styles={customStyles}
                              isDisabled={!selectedItem}
                              className="w-48"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {userRole === "foreman" ? (
                  ""
                ) : (
                  <div className="mt-4">
                    <p className="text-lg font-semibold text-gray-200">
                      Items Total: ${totalCost.toFixed(2)}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          <div>
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-sm font-medium text-gray-400">Surcharges</h4>
              <button
                onClick={handleAddSurcharge}
                className="px-4 py-2 text-sm font-medium text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
              >
                Add Surcharge
              </button>
            </div>
            {surcharges.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-600">
                  {surcharges.map((surcharge, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-500 transition-colors duration-200"
                    >
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                        <input
                          type="text"
                          value={surcharge.description}
                          onChange={(e) =>
                            handleSurchargeChange(
                              index,
                              "description",
                              e.target.value,
                            )
                          }
                          placeholder="e.g., Delivery Fee"
                          className="w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                        <div className="relative">
                          <NumericFormat
                            value={surcharge.price}
                            onValueChange={(values) =>
                              handleSurchargeChange(index, "price", values)
                            }
                            allowNegative={true}
                            decimalScale={2}
                            fixedDecimalScale
                            thousandSeparator=","
                            prefix="$"
                            className="w-24 pl-6 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-right">
                        <button
                          onClick={() => handleRemoveSurcharge(index)}
                          className="text-red-400 hover:text-red-500"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-gray-400">No surcharges added.</p>
            )}
            {userRole == "foreman" ? (
              ""
            ) : (
              <div className="mt-4 p-4 bg-gray-600 rounded-lg border border-gray-600 max-w-72">
                <p className="text-lg font-semibold text-gray-200">
                  Total Cost:{" "}
                  <span className="text-orange-400">
                    $
                    {totalCost.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </p>
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400">
              Order Attachments
            </label>
            <div className="mt-1">
              {(order.orderAttachments || []).map((att, idx) => (
                <div key={idx} className="text-sm text-gray-200">
                  <a
                    href={att.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-orange-400 hover:text-orange-300"
                  >
                    {att.name}
                  </a>
                </div>
              ))}
              {newOrderAttachments.map((att, idx) => (
                <div key={idx} className="text-sm text-gray-200">
                  {att.name}
                </div>
              ))}
            </div>
            <input
              type="file"
              multiple
              onChange={handleOrderFileUpload}
              className="mt-2 block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-600 file:text-gray-200 hover:file:bg-gray-500"
              accept=".pdf"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400">
              Delivery Attachments
            </label>
            <div className="mt-1">
              {(order.deliveryAttachments || []).map((att, idx) => (
                <div key={idx} className="text-sm text-gray-200">
                  <a
                    href={att.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-orange-400 hover:text-orange-300"
                  >
                    {att.name}
                  </a>
                </div>
              ))}
              {newDeliveryAttachments.map((att, idx) => (
                <div key={idx} className="text-sm text-gray-200">
                  {att.name}
                </div>
              ))}
            </div>
            <input
              type="file"
              multiple
              onChange={handleDeliveryFileUpload}
              className="mt-2 block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-600 file:text-gray-200 hover:file:bg-gray-500"
              accept=".pdf"
            />
          </div>
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() =>
                navigate(`/projects/${id}`, {
                  state: { initialTab: "Materials" },
                })
              }
              className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
            >
              Cancel
            </button>
            {order.status !== "delivered" &&
              order.status !== "void" &&
              order.status !== "confirmed" && (
                <button
                  type="button"
                  onClick={handleConfirmOrder}
                  className="px-4 py-2 text-sm font-medium text-gray-200 bg-green-500 rounded-md hover:bg-green-600 transition-colors duration-200"
                >
                  Confirm Order
                </button>
              )}
            {order.status !== "delivered" && order.status !== "void" && (
              <button
                type="button"
                onClick={handleVoidOrder}
                className="px-4 py-2 text-sm font-medium text-gray-200 bg-red-400 rounded-md hover:bg-red-500 transition-colors duration-200"
              >
                Void Order
              </button>
            )}
            <button
              type="button"
              onClick={handleUpdateOrder}
              className="px-4 py-2 text-sm font-medium text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
            >
              Update Order
            </button>
            <button
              type="button"
              onClick={handleSendEmail}
              className="px-4 py-2 text-sm font-medium text-gray-200 bg-indigo-500 rounded-md hover:bg-indigo-600 transition-colors duration-200"
            >
              Send Email
            </button>
            <button
              type="button"
              onClick={() => generateDocx(false)}
              className="px-4 py-2 text-sm font-medium text-gray-200 bg-blue-400 rounded-md hover:bg-blue-500 transition-colors duration-200"
            >
              PDF Order
            </button>
          </div>
        </div>
      </div>
      {showEmailForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-700 p-6 rounded-lg shadow-lg w-full max-w-lg border border-gray-600">
            <h2 className="text-xl font-semibold text-orange-400 mb-4">
              Send Email
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  From
                </label>
                <input
                  type="email"
                  value={userEmail}
                  disabled
                  className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  To
                </label>
                <input
                  type="text"
                  value={emailForm.to}
                  onChange={(e) =>
                    setEmailForm({ ...emailForm, to: e.target.value })
                  }
                  placeholder="recipient@example.com, another@example.com"
                  className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Body
                </label>
                <textarea
                  value={emailForm.body}
                  onChange={(e) =>
                    setEmailForm({ ...emailForm, body: e.target.value })
                  }
                  rows="5"
                  className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Signature
                </label>
                <textarea
                  value={emailForm.signature}
                  onChange={(e) =>
                    setEmailForm({ ...emailForm, signature: e.target.value })
                  }
                  rows="3"
                  className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
              </div>
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEmailForm(false);
                    setPdfBuffer(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleEmailSubmit}
                  className="px-4 py-2 text-sm font-medium text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                  disabled={!pdfBuffer}
                >
                  Send Email
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
